import { Chip, FontAwesomeIcon } from '@appcharge/shared-ui';
import { ColumnDef } from '@tanstack/react-table';
import { PromoCode } from 'common/contracts';
import dayjs from 'dayjs';
import { EToasterStatus, showToast } from 'utils/showToast';
import { renderActiveStatusChip } from 'utils/statusChipUtils';

import Button from 'components/Button/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuTrigger
} from 'components/ui/dropdown-menu';

import { DATE_TIME_FORMAT } from '../../../constants/constants';


export type PromoCodeTableRow = PromoCode & {
  isStatusChanging?: boolean;
  originalActiveState?: boolean;
  createdAt?: string;
};

interface PromoCodesTableColumnsParams {
  onCancel: (name: string) => void;
  onToggleActive: (name: string, currentActive: boolean, originalActiveState?: boolean) => void;
}

export const PROMO_CODES_TABLE_COLUMNS = ({
  onCancel,
  onToggleActive
}: PromoCodesTableColumnsParams): ColumnDef<PromoCodeTableRow>[] => [
  {
    accessorKey: 'name',
    header: 'Name',
    cell: ({ row }) => (
      <div className="group flex items-center justify-between w-full">
        <span data-testid="name">{row.original.name}</span>
        <FontAwesomeIcon
          icon="fa-regular fa-copy"
          className="cursor-pointer text-base-mutedForeground hover:text-base-foreground opacity-0 group-hover:opacity-100 transition-opacity"
          onClick={() => {
            navigator.clipboard.writeText(row.original.name);
            showToast({
              message: 'Promo code copied to clipboard',
              status: EToasterStatus.SUCCESS
            });
          }}
        />
      </div>
    ),
    enableSorting: true
  },
  {
    accessorKey: 'promoCodeAvailability',
    header: 'Redemptions',
    cell: ({ row }) => {
      const availability = row.original.promoCodeAvailability?.[0];
      if (!availability) return '';
      const max = availability.maxRedemptions || row.original.maxRedemptions;
      return (
        <span data-testid="availability">
          {availability.redemptions} / {max}
        </span>
      );
    }
  },
  {
    accessorKey: 'createdAt',
    header: 'Created Date & Time',
    cell: ({ row }) => {
      const createdAt = row.original.createdAt;
      return createdAt ? (
        <span data-testid="creation-date">
          {dayjs(createdAt).utc().format(DATE_TIME_FORMAT)}
        </span>
      ) : (
        'N/A'
      );
    },
    enableSorting: true
  },
  {
    accessorKey: 'active',
    header: 'Status',
    cell: ({ getValue, row }) => {
      if (row.original.fresh || row.original.isStatusChanging) {
        return (
          <Chip variant="flat" status="warning" className="capitalize text-orange-500">
            Pending
          </Chip>
        );
      }
      const value = getValue() as boolean;
      return renderActiveStatusChip(value);
    },
    enableSorting: true
  },
  {
    id: 'actions',
    header: '',
    cell: ({ row }) => {
      return (
        <div className="flex justify-end w-full">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <FontAwesomeIcon icon="fa-solid fa-ellipsis-vertical" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuPortal>
              <DropdownMenuContent align="end" className="bg-base-popover">
                {row.original.fresh ? (
                  <DropdownMenuItem
                    className="cursor-pointer hover:bg-[#F4F4F5]"
                    onClick={() => onCancel(row.original.name)}
                  >
                    Cancel
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem
                    className="cursor-pointer hover:bg-[#F4F4F5]"
                    onClick={() =>
                      onToggleActive(
                        row.original.name,
                        row.original.active,
                        row.original.originalActiveState
                      )
                    }
                  >
                    {row.original.active ? 'Deactivate' : 'Activate'}
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenuPortal>
          </DropdownMenu>
        </div>
      );
    }
  }
];
